import React, { useState, useEffect, useRef } from 'react';

import img1 from '../assets/customerfeedbackimages/1.jpeg';
import img2 from '../assets/customerfeedbackimages/2.jpeg';
import img3 from '../assets/customerfeedbackimages/3.jpeg';
import img4 from '../assets/customerfeedbackimages/4.jpeg';
import img5 from '../assets/customerfeedbackimages/5.jpeg';
import img6 from '../assets/customerfeedbackimages/6.jpeg';
import img7 from '../assets/customerfeedbackimages/7.jpeg';
import img8 from '../assets/customerfeedbackimages/8.jpeg';
import img9 from '../assets/customerfeedbackimages/9.jpeg';
import img10 from '../assets/customerfeedbackimages/10.jpeg';
import img11 from '../assets/customerfeedbackimages/11.jpeg';
import img12 from '../assets/customerfeedbackimages/12.jpeg';
import img13 from '../assets/customerfeedbackimages/13.jpeg';
import img14 from '../assets/customerfeedbackimages/14.jpeg';
import img15 from '../assets/customerfeedbackimages/15.jpeg';
import img16 from '../assets/customerfeedbackimages/16.jpeg';
import img17 from '../assets/customerfeedbackimages/17.jpeg';
import img18 from '../assets/customerfeedbackimages/18.jpeg';
import imgWA1 from '../assets/customerfeedbackimages/WhatsApp Image 2026-04-02 at 4.50.34 PM.jpeg';
import imgWA2 from '../assets/customerfeedbackimages/WhatsApp Image 2026-04-02 at 4.50.34 PM (1).jpeg';
import imgWA3 from '../assets/customerfeedbackimages/WhatsApp Image 2026-04-02 at 4.50.35 PM.jpeg';

const images = [
  img1, img2, img3, img4, img5, img6, img7, img8, img9, img10,
  img11, img12, img13, img14, img15, img16, img17, img18,
  imgWA1, imgWA2, imgWA3,
];

const GAP = 8; // px

function getVisible() {
  return window.innerWidth >= 640 ? 5 : 2;
}

const CustomerFeedbackSlider: React.FC = () => {
  const [visible, setVisible] = useState(getVisible);
  const [startIndex, setStartIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setVisible(getVisible());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const totalGroups = Math.ceil(images.length / visible);
  const currentGroup = Math.floor(startIndex / visible);

  const slide = (newIndex: number) => {
    if (animating) return;
    setAnimating(true);
    setStartIndex(newIndex);
    setTimeout(() => setAnimating(false), 400);
  };

  const prev = () => {
    const newIndex = startIndex === 0 ? images.length - visible : Math.max(0, startIndex - visible);
    slide(newIndex);
  };

  const next = () => {
    const newIndex = startIndex + visible >= images.length ? 0 : startIndex + visible;
    slide(newIndex);
  };

  const goToGroup = (group: number) => {
    slide(group * visible);
    resetAutoPlay();
  };

  const resetAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(next, 3500);
  };

  useEffect(() => {
    autoPlayRef.current = setInterval(next, 3500);
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      diff > 0 ? next() : prev();
      resetAutoPlay();
    }
    touchStartX.current = null;
  };

  const visibleImages = images.slice(startIndex, startIndex + visible);
  // Pad with wraparound if near the end
  const padded = visibleImages.length < visible
    ? [...visibleImages, ...images.slice(0, visible - visibleImages.length)]
    : visibleImages;

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-minto-500 rounded-full" />
          <h2 className="text-base font-semibold text-gray-800">Happy Customers</h2>
        </div>
      </div>

      {/* Slider */}
      <div
        className="relative"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Left arrow */}
        <button
          onClick={() => { prev(); resetAutoPlay(); }}
          className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 shadow-md text-gray-600 hover:text-minto-600 hover:border-minto-300 rounded-full w-7 h-7 flex items-center justify-center transition-all"
          aria-label="Previous"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Track */}
        <div
          ref={trackRef}
          className="overflow-hidden rounded-xl mx-4"
        >
          <div
            className="flex transition-all duration-400 ease-in-out"
            style={{ gap: `${GAP}px` }}
          >
            {padded.map((src, i) => (
              <div
                key={`${startIndex}-${i}`}
                className="flex-shrink-0 cursor-pointer group"
                style={{ width: `calc(${100 / visible}% - ${(GAP * (visible - 1)) / visible}px)` }}
                onClick={() => setSelectedIndex(startIndex + i < images.length ? startIndex + i : i - visibleImages.length)}
              >
                <div className="relative overflow-hidden rounded-lg shadow-sm border border-gray-100 bg-gray-50 group-hover:shadow-md group-hover:border-minto-200 transition-all duration-200"
                  style={{ aspectRatio: '3/4' }}
                >
                  <img
                    src={src}
                    alt={`Customer feedback ${startIndex + i + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Shimmer overlay on hover */}
                  <div className="absolute inset-0 bg-minto-600/0 group-hover:bg-minto-600/10 transition-colors duration-200 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right arrow */}
        <button
          onClick={() => { next(); resetAutoPlay(); }}
          className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 shadow-md text-gray-600 hover:text-minto-600 hover:border-minto-300 rounded-full w-7 h-7 flex items-center justify-center transition-all"
          aria-label="Next"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-3">
        {Array.from({ length: totalGroups }).map((_, i) => (
          <button
            key={i}
            onClick={() => goToGroup(i)}
            className={`rounded-full transition-all duration-300 ${
              i === currentGroup
                ? 'bg-minto-500 w-5 h-1.5'
                : 'bg-gray-300 w-1.5 h-1.5'
            }`}
            aria-label={`Go to group ${i + 1}`}
          />
        ))}
      </div>

      {/* Lightbox */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedIndex(null)}
        >
          <div className="relative max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <img
              src={images[selectedIndex]}
              alt={`Customer feedback ${selectedIndex + 1}`}
              className="w-full rounded-2xl shadow-2xl"
            />
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute -top-3 -right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg text-gray-600 hover:text-red-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Lightbox prev/next */}
            <button
              onClick={() => setSelectedIndex((selectedIndex - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setSelectedIndex((selectedIndex + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerFeedbackSlider;
